// Shared date utility functions for consistent formatting across the app

import { logError } from './errorUtils';

// Helper function to check if two dates are the same day
export const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

// Helper function to format time ago (e.g., "5m ago", "2h ago", "3d ago")
export const formatTimeAgo = (date: Date): string => {
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
  
  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;
  
  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays}d ago`;
};

// Helper function to format date range for display
export const formatDateRangeForDisplay = (
  startDate: Date,
  endDate?: Date
): string => {
  if (!startDate) return '';

  if (!endDate || isSameDay(startDate, endDate)) {
    // Same day - show single date
    return startDate.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  // Different days - show range
  const startFormatted = startDate.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  const endFormatted = endDate.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  return `${startFormatted} - ${endFormatted}`;
};

// Helper function to convert date string to Date object (supports YYYY-MM-DD backend format)
export const parseDate = (dateString: string): Date => {
  // Check if it's YYYY-MM-DD format (backend format)
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return new Date(dateString);
  }

  // Check if it's DD-MM-YYYY format (legacy format)
  if (dateString.match(/^\d{2}-\d{2}-\d{4}$/)) {
    const [day, month, year] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  // Fallback to direct Date constructor
  return new Date(dateString);
};

// Helper function to format a single date with user's local format
export const formatDate = (date: Date | undefined): string => {
  if (!date) return '';
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

// Helper function to format a date range (e.g., "15 Mar 2024 - 20 Mar 2024")
export const formatDateRange = (
  startDate: Date | undefined,
  endDate: Date | undefined
): string => {
  if (!startDate) return 'Select dates';
  return `${formatDate(startDate)} - ${formatDate(endDate || startDate)}`;
};

// Helper function to format YYYY-MM-DD backend date to user's local format
export const formatDateWith3CharMonth = (dateString: string): string => {
  try {
    const date = parseDate(dateString);

    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    }

    // Fallback to original string if parsing fails
    return dateString;
  } catch (error) {
    logError(error, 'formatDateWith3CharMonth');
    return dateString;
  }
};

// Helper function to calculate duration between two YYYY-MM-DD dates
export const calculateDuration = (
  startDateString: string,
  endDateString: string
): number => {
  try {
    const start = parseDate(startDateString);
    const end = parseDate(endDateString);
    const days = Math.ceil(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
    );
    return days > 0 ? days : 1; // Ensure at least 1 day
  } catch (error) {
    console.error(
      'Duration calculation error:',
      error,
      'for dates:',
      startDateString,
      endDateString
    );
    return 1; // Fallback to 1 day
  }
};

// Helper function to format dates for URL display (DD-MM-YYYY format for URLs)
export const formatDateForURL = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Helper function to convert user input date to YYYY-MM-DD for backend
export const formatDateForBackend = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Validate date string is in YYYY-MM-DD format (backend format)
export const isValidBackendDate = (dateString: string): boolean => {
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateString)) return false;

  try {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  } catch (error) {
    return false;
  }
};
